// The one company read (pipeline RC1, module 10 U352), as the portal receives it on the company record's `read`.
// Written out here because the portal does not import the API's code; the API's own types and the words they carry
// are apps/api/src/records/company-read.ts, which this file must follow field for field. Every sentence the record's
// head prints about money, dates and risk comes from the read's own `words`, so the record, the rundown and the line
// say the same thing in the same words (U352).

export type Month = string;
export type Day = string;

export interface MoneyLine { customer: string; product: string; vertical: string; amount: number }

export interface Billed {
  total: number | null;
  window: { from: Month; to: Month } | null;
  lines: MoneyLine[];
  shared: Array<{ customer: string; product: string; others: string[]; unsplitMonths: number }>;
  words: { label: string; note: string | null };
}

export interface Forecast {
  total: number | null;
  window: { from: Month; to: Month } | null;
  lines: MoneyLine[];
  split: { month: Month; customer: string; ours: number; others: Array<{ name: string; amount: number }> } | null;
  ifEnds: { agreement: string; on: Day; total: number } | null;
  notCounted: Array<{ holder: string; names: string[]; amount: number | null; ends: Day | null }>;
  words: { note: string | null; ifEnds: string | null; notCounted: string | null };
}

export interface DealProposal { id: string; headline: string; heardAt: string }

export interface Potential {
  total: number | null;
  byProduct: Array<{ product: string; amount: number }>;
  deals: number;
  proposals: DealProposal[];
  words: { label: string; note: string | null };
}

export interface DatePlate { on: Day; kind: 'notice' | 'end'; red: boolean; words: string }

export interface Risk {
  share: number; agreement: string; noticeBy: Day | null; endsOn: Day; daysLeft: number;
  deal: { id: string; title: string; organisationId: string; organisationName: string; stage: string; expectedCloseOn: Day | null; daysPast: number | null; ownerName: string | null; onThisRecord: boolean } | null;
  words: string;
}

export interface AgreementInForce {
  id: string; organisationId: string; product: string | null; vertical: string | null; competition: string | null; rightsHolder: string | null;
  endsOn: Day | null; renews: boolean; noticeBy: Day | null; monthlyFee: string | null; hasPaper: boolean; words: string;
  autoRenew: boolean | null; noticeDays: number | null; start: Day | null;
}

export interface FinanceTerm { customer: string; billingCompany: string | null; product: string | null; start: Day | null; end: Day | null; increasePct: number | null }

export type ProductReading =
  | { state: 'not_built' }
  | { state: 'none'; words: string; firstReadingOn: Month | null }
  | { state: 'read'; chip: string; sentence: string; month: Month };

export interface CompanyRead {
  asOf: Day;
  company: { id: string; name: string; accountType: string | null; handledBy: string | null; parent: { id: string; name: string } | null; brands: Array<{ id: string; name: string }> };
  money: { available: boolean; billed: Billed; forecast: Forecast; potential: Potential };
  dates: DatePlate[];
  risk: Risk | null;
  agreements: { inForce: AgreementInForce[]; financeOnly: Array<FinanceTerm & { words: string }> };
  owed: { ours: number; theirs: number; late: number; notOnRecord: number };
  readings: { oneTrade: ProductReading; oneVideo: ProductReading; inFront: ProductReading };
  /** Pipeline RC7 (track B): the stored logo, and whether one was looked for; absent from a build before RC7. */
  logo?: { url: string; source: 'hand' | 'site'; width: number | null; height: number | null } | null;
  logoSearch?: 'found' | 'none_found' | 'not_yet' | 'no_domain';
  /** Pipeline RC9 (track A): a person's note that the client went direct to InFront; absent from a build before RC9. */
  inFrontRoute?: InFrontRoute | null;
  /** InFront's own record and who handles it, for "Ask <owner> to confirm it went direct" (RC14); null on InFront's record. */
  inFrontRecord?: { id: string; name: string; owner: { principalId: string; name: string } | null } | null;
  /** Pipeline RC10 (track A): what says how the client takes InFront's streams; confirmed oldest first, proposed strongest
   * first; each reader is given only what they may read. Absent from a build before RC10. */
  inFrontEvidence?: { confirmed: InFrontEvidence[]; proposed: InFrontEvidence[] };
  /** Pipeline RC13 (track A): the supply side of a company typed Rights Holder; null for any other, absent before RC13. */
  rightsHolder?: RightsHolderRead | null;
}

type Words = { label: string; note: string | null };

/** RC13's rights-holder read (apps/api/src/records/rights-holder-read.ts), field for field. */
export interface RightsHolderRead {
  linked: Array<{ id: number; name: string | null }>;
  pay:
    | { state: 'read'; product: string; share: number; billed: PayWindow | null; forecast: PayWindow | null; words: Words }
    | { state: 'not_read'; words: Words };
  potential: Potential;
  next: { on: Day; kind: 'notice' | 'end'; words: string } | null;
  agreements: AgreementInForce[];
  takers:
    | { state: 'read'; year: number; through: Day | null; operators: Taker[]; shares: string[]; words: Words }
    | { state: 'not_linked'; words: Words };
  supply:
    | { state: 'read'; source: string; since: Month; booked: number; started: number; delivered: number | null; through: Day | null; words: Words }
    | { state: 'none' | 'not_linked'; words: Words };
  /** Only on InFront's own record: its clients' four readings (brief 4b). */
  inFrontClients: InFrontClients | null;
}
export interface PayWindow { window: { from: Month; to: Month }; billedByIgame: number; paid: number; byOperator: Array<{ name: string; paid: number }> }
export interface Taker { name: string; organisationId: string | null; operatorId: number; internal: boolean; eventsTaken: number; eventsListed: number; views: number }
export type InFrontClientReading = 'on_integration' | 'calls_falling' | 'no_calls' | 'went_direct';
export interface InFrontClients {
  asOf: Month | null;
  counts: { onIntegration: number; callsFalling: number; noCalls: number; wentDirect: number };
  clients: Array<{ organisationId: string | null; name: string; reading: InFrontClientReading; chip: string; sentence: string; viewsJanJun: number }>;
  words: { summary: string };
}

/** One piece of RC10's evidence: a passage from a mail, a chat or a call, with when and by whom it was said. */
export interface InFrontEvidence {
  id: string; organisationId: string; signalId: string | null; meetingId: string | null; kind: string; subject: string | null;
  occurredAt: string; passage: string; saidBy: string | null; state: string; route: string | null; strength: number | null;
  decidedBy: string | null; decidedAt: string | null;
  /** The page that opens where it was said, as the record's threads carry it; absent or null where none does. */
  opens?: { page: 'item' | 'meeting' | 'chat'; id: string } | null;
}

/** RC9's note: who confirmed the client went direct to InFront, who noted it and when. */
export interface InFrontRoute { id: string; organisationId: string; confirmedBy: string; notedBy: string | null; notedAt: string }

/** A company's name as its owner: "Bet365's", but "Genius Sports'" where the name ends in s (seen on live, 7 Oct). */
export function whose(name: string): string {
  const n = name.trim();
  return /s$/i.test(n) ? `${n}'` : `${n}'s`;
}

/** "£956,780": whole pounds, as the API's words print them. */
export function gbp(n: number): string {
  const r = Math.round(n);
  return `${r < 0 ? '-' : ''}£${Math.abs(r).toLocaleString('en-GB')}`;
}

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2 Nov 2026": a plate's date, never a month written as "Nov 26" (BUGS.md D178). */
export function plateDay(d: Day): string {
  const [y, m, dd] = d.split('-').map(Number);
  return `${dd} ${SHORT[(m ?? 1) - 1]} ${y}`;
}

/** The verdict rows of the head (brief section 4, item 4): one per product the read has a reading for. */
export function verdictRows(read: CompanyRead): Array<{ product: string; anchor: string; chip: string; sentence: string }> {
  const out: Array<{ product: string; anchor: string; chip: string; sentence: string }> = [];
  const add = (product: string, anchor: string, r: ProductReading) => {
    if (r.state === 'read') out.push({ product, anchor, chip: r.chip, sentence: r.sentence });
    else if (r.state === 'none') out.push({ product, anchor, chip: 'No reading yet', sentence: r.words });
  };
  add('OneVideo', 'a-onevideo', read.readings.oneVideo);
  add('OneTrade', 'a-onetrade', read.readings.oneTrade);
  add('InFront', 'a-infront', read.readings.inFront);
  return out;
}
